import { changeArrayItemIndex, asyncCatch, getLabelFromStorage } from '@pb/utils'
import { omit, isEmpty } from 'ramda'

import {
  CHECKOUT_TRACKING_SECTIONS,
  CHECKOUT_ERROR_CODES,
  DELIVERY_METHODS,
  DELIVERY_PHONE_NUMBER,
  SHOW_NOTIFICATION_TIME,
  TECHNICAL_ERROR_LABELS,
  OBSERVED_ADDRESS_FORM_ERRORS,
  CHECKOUT_TRACKING_ERROR_TYPES,
} from 'features/checkout/constants'
import {
  setShouldShowProductsDetails,
  updateDeliveryModesSuccess,
  selectDeliverySpeedFail,
  selectDeliverySpeedStart,
  selectDeliverySpeedSuccess,
  setSelectedDeliveryMethod,
  setSelectedDeliverySpeed,
  setSelectedHomeDeliveryAddressId,
  setHasUndeliverableItems,
} from 'features/checkout/duck/actions'
import { denormalizePickupPlace, normalizeContextPartial } from 'features/checkout/duck/normalize'
import checkoutSelectors, { getIsOnlyBOPISItems } from 'features/checkout/duck/selectors'
import { patchDelivery } from 'features/checkout/requests'
import {
  Context,
  DeliveryMode,
  DeliveryPatchResponse,
  DeliveryRequestData,
} from 'features/checkout/types'
import { showNotification } from 'features/notifications/duck/actions'
import { setOrderTotals } from 'features/orderTotals/duck/actions'
import getErrorData from 'helpers/getErrorData'
import { ThunkResult } from 'redux/types'
import { ApiError } from 'types'

import { trackError } from '../track'

const operationRearrangeDeliveryModes = (): ThunkResult<void> => (dispatch, getState) => {
  const delivery = checkoutSelectors.getCheckoutDelivery(getState())

  const { deliveryModesAsArray, selectedDeliverySpeed } = delivery

  if (!deliveryModesAsArray) {
    return
  }

  const selectedItemIndex = deliveryModesAsArray.findIndex(
    (mode: DeliveryMode) => selectedDeliverySpeed === mode.code,
  )
  const sortedDeliveryModeList: DeliveryMode[] =
    selectedItemIndex > -1
      ? changeArrayItemIndex(selectedItemIndex, 0, deliveryModesAsArray)
      : deliveryModesAsArray

  dispatch(
    updateDeliveryModesSuccess({
      ...delivery,
      deliveryModesAsArray: sortedDeliveryModeList,
    }),
  )
}

export const operationSelectDeliverySpeed =
  (): ThunkResult<Promise<void>> => async (dispatch, getState) => {
    const state = getState()
    const selectedHomeDeliveryAddressId = checkoutSelectors.getSelectedHomeDeliveryAddressId(state)
    const selectedPickupPoint = checkoutSelectors.getSelectedPickupPoint(state)
    const selectedCnCPoint = checkoutSelectors.getSelectedCnCPoint(state)
    const previouslyUsedPickupPlace = checkoutSelectors.getPreviouslyUsedPickupPlace(state)
    const selectedDeliveryMethod = checkoutSelectors.getSelectedDeliveryMethod(state)
    const selectedDeliverySpeed = checkoutSelectors.getSelectedDeliverySpeed(state)
    const phoneNumber = checkoutSelectors.getDeliveryPhoneNumber(state)
    const isPUP = selectedDeliveryMethod === DELIVERY_METHODS.pickupPoint
    const isCnC = selectedDeliveryMethod === DELIVERY_METHODS.clickAndCollect
    const isHomeDelivery = selectedDeliveryMethod === DELIVERY_METHODS.homeDelivery
    const isOnlyBOPISItems = getIsOnlyBOPISItems(state)
    const pickupPlace =
      isPUP && selectedPickupPoint
        ? checkoutSelectors.getSelectedPickupPointDetailsFromRawResponse(state)
        : previouslyUsedPickupPlace
    const CnCPlace = selectedCnCPoint
      ? omit(['deliveryModes'], selectedCnCPoint)
      : previouslyUsedPickupPlace

    const isTimeSlotDelivery = selectedDeliveryMethod === DELIVERY_METHODS.timeSlotDelivery
    const selectedTimeSlot = checkoutSelectors.getSelectedTimeSlot(state)

    dispatch(selectDeliverySpeedStart())

    let deliveryData: DeliveryRequestData = { selectedDeliveryMode: selectedDeliverySpeed }

    if (!isEmpty(phoneNumber) && !!phoneNumber?.prefix && !!phoneNumber?.cellPhone) {
      deliveryData = { ...deliveryData, phoneNumber }
    }

    if (isPUP) {
      deliveryData = {
        ...deliveryData,
        pickupPlace,
      }
    }

    if (isCnC && !isOnlyBOPISItems) {
      // @ts-expect-error ts-migrate(2322) FIXME: Type '{ pickupPlace: { postcode: any; townCity: an... Remove this comment to see the full error message
      deliveryData = { ...deliveryData, pickupPlace: denormalizePickupPlace(CnCPlace) }
    }

    if (isOnlyBOPISItems) {
      deliveryData = { phoneNumber }
    }

    if (isHomeDelivery) {
      deliveryData = { ...deliveryData, addressId: selectedHomeDeliveryAddressId }
    }

    if (isTimeSlotDelivery !== undefined && selectedTimeSlot) {
      deliveryData.timeslot = {
        date: selectedTimeSlot.date,
        intervalCode: selectedTimeSlot.intervalCode,
      }
    }

    const [error, patchDeliveryResponse]: [ApiError, DeliveryPatchResponse] = await asyncCatch(
      patchDelivery(deliveryData),
    )

    if (error) {
      const { isTechnicalError, isClientError, code, target: initialTarget } = getErrorData(error)
      // Replace phone number target because of difference in FE form schema and BE response
      // BE set target like "phoneNumber.cellPhone", but FE doesn't split number by prefix and number itself
      const target = initialTarget?.includes(DELIVERY_PHONE_NUMBER)
        ? DELIVERY_PHONE_NUMBER
        : initialTarget
      const getClientErrorMessage = (): string => {
        if (target === DELIVERY_PHONE_NUMBER) {
          return getLabelFromStorage('checkout.error.delivery.phone.number')
        }
        if (code === CHECKOUT_ERROR_CODES.invalidDeliveryAddress) {
          return getLabelFromStorage(TECHNICAL_ERROR_LABELS.invalidAddress)
        }
        return getLabelFromStorage(TECHNICAL_ERROR_LABELS.generalDelivery)
      }

      const message = isClientError
        ? getClientErrorMessage()
        : getLabelFromStorage(TECHNICAL_ERROR_LABELS.general)
      if (isClientError) {
        const isInvalidDeliveryAddress = code === CHECKOUT_ERROR_CODES.invalidDeliveryAddress
        if (isInvalidDeliveryAddress) {
          dispatch(
            selectDeliverySpeedFail([
              { code: OBSERVED_ADDRESS_FORM_ERRORS.invalidDeliveryAddress },
            ]),
          )
        } else {
          dispatch(selectDeliverySpeedFail([{ code: '', target, message }]))
        }
      }
      if (isTechnicalError) {
        dispatch(selectDeliverySpeedFail([]))
      }
      dispatch(
        showNotification({
          content: message,
          type: 'error',
          timespan: SHOW_NOTIFICATION_TIME,
        }),
      )
      dispatch(
        trackError({
          section: CHECKOUT_TRACKING_SECTIONS.deliverySpeed,
          message,
          id: code,
          errorType: CHECKOUT_TRACKING_ERROR_TYPES.system,
        }),
      )
      return
    }

    const mainParcelAddress =
      patchDeliveryResponse?.delivery?.consignmentDeliveryDatas?.[0]?.deliveryAddress

    const contextToUpdate = {
      ...{
        ...patchDeliveryResponse,
        ...(mainParcelAddress && { cart: { deliveryAddress: mainParcelAddress } }),
        payment: {
          ...patchDeliveryResponse.payment,
        },
      },
    } as Partial<Context>

    const normalizedContext = dispatch(
      normalizeContextPartial({
        ...contextToUpdate,
      }),
    )

    const undeliverableHazmatItems = checkoutSelectors.getCartUndeliverableHazmatItems(getState())
    const isSelectedDeliverySpeedSupportingHazmat =
      checkoutSelectors.getIsSelectedDeliverySpeedSupportingHazmat(getState())
    const isSelectedDeliveryPlaceSupportHazmat =
      checkoutSelectors.getIsSelectedDeliveryPlaceSupportHazmat(getState())
    if (
      undeliverableHazmatItems.length > 0 &&
      (!isSelectedDeliverySpeedSupportingHazmat || !isSelectedDeliveryPlaceSupportHazmat)
    ) {
      dispatch(setShouldShowProductsDetails(true))
      dispatch(setHasUndeliverableItems(true))
      dispatch(selectDeliverySpeedFail([]))
      return
    }
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
    dispatch(selectDeliverySpeedSuccess(normalizedContext))
    dispatch(operationRearrangeDeliveryModes())
    dispatch(setOrderTotals(patchDeliveryResponse.orderTotals))
  }

export const operationSelectHomeDelivery =
  (deliverySpeedCode: string): ThunkResult<void> =>
  (dispatch, getState) => {
    dispatch(setSelectedDeliverySpeed(deliverySpeedCode))
    dispatch(setSelectedDeliveryMethod(DELIVERY_METHODS.homeDelivery))
    const deliveryAddress = checkoutSelectors.getDeliveryAddress(getState())
    dispatch(setSelectedHomeDeliveryAddressId(deliveryAddress?.id))
    dispatch(operationSelectDeliverySpeed())
  }
